import { useTranslation } from "react-i18next";

import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

import { PreliminaryBadge } from "../adherence/PreliminaryBadge";
import { CoaStatusBadge } from "../chartOfAccounts/CoaStatusBadge";
import { useCoaStatusLabels } from "../chartOfAccounts/coaLabels";
import type { ActId } from "../acts";
import { ADHERENCE_BAR, ADHERENCE_COLORS, makeAdherenceStatusMeta } from "../diagnosticMeta";
import { Donut } from "../primitives/Donut";
import { StackedBar } from "../primitives/StackedBar";
import { NO_VALUE } from "../sectionState";
import { COA_STATUSES, COA_STATUS_COLORS, formatPercent } from "../selectors/chartOfAccounts";
import { SummaryBlock } from "./SummaryBlock";
import { SummaryLegend, Swatch } from "./SummaryLegend";
import { adherenceUnavailableHint } from "./summaryText";
import type { DiagnosticSummary } from "./summaryData";

/**
 * Bloco 2 · O diagnóstico (EF-DIAG-004 RF-04): a aderência ao padrão do
 * `adherence_index` (EF-FUNC-011) e a do plano de contas (EF-FUNC-009).
 *
 * Os percentuais da barra e da frase são os do backend, já fechados em 100 pelo
 * maior resto: o Resumo não arredonda de novo.
 */
export function DiagnosisBlock({
  summary,
  act,
  onGoToAct,
}: {
  summary: DiagnosticSummary["diagnosis"];
  act: ActId;
  onGoToAct: (act: ActId) => void;
}) {
  const { t } = useTranslation();
  const statusLabels = useCoaStatusLabels();
  const fitLabels = makeAdherenceStatusMeta(t);
  const { adherence, chartOfAccounts: coa } = summary;
  const total = adherence.kind === "counted" ? adherence.index.rows.consolidated : undefined;
  const preliminary = adherence.kind === "counted" && adherence.index.preliminary;

  function reading(): string | undefined {
    if (adherence.kind !== "counted" || !total || total.base === 0) return undefined;
    const { standard, extension, gap } = total;
    if (standard.pct == null || extension.pct == null || gap.pct == null) return undefined;
    return tp(
      t,
      adherence.index.gaps.total,
      "{{standard}} of every 100 items run on the Public standard; {{extension}} with a Clean Core extension and {{gap}} with a GAP ({{count}} GAP registered).",
      "{{standard}} of every 100 items run on the Public standard; {{extension}} with a Clean Core extension and {{gap}} with a GAP ({{count}} GAPs registered).",
      { standard: standard.pct, extension: extension.pct, gap: gap.pct },
    );
  }

  return (
    <SummaryBlock
      id="diagnosis"
      index={2}
      overline={t("The diagnosis")}
      title={t("How much the standard covers")}
      act={act}
      onGoToAct={onGoToAct}
      reading={reading()}
    >
      <div className="flex flex-wrap items-start gap-x-6 gap-y-5">
        <div className="flex min-w-[180px] flex-1 basis-[200px] flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {t("Standard fit")}
            {preliminary && <PreliminaryBadge />}
          </div>
          <div
            data-testid="summary-standard-fit"
            className="text-[40px] font-extrabold leading-none tabular-nums text-[#08254c]"
          >
            {total?.standard.pct == null ? NO_VALUE : `${total.standard.pct}%`}
          </div>
          {total && total.base > 0 ? (
            <>
              <StackedBar
                height="h-3.5"
                segments={ADHERENCE_COLORS.map((color) => ({
                  key: color,
                  label: fitLabels[color],
                  className: ADHERENCE_BAR[color],
                  value: total[color].count,
                }))}
              />
              <SummaryLegend
                testIdPrefix="summary-fit"
                items={ADHERENCE_COLORS.map((color) => ({
                  key: color,
                  label: fitLabels[color],
                  value: formatCount(total[color].count),
                  share: total[color].pct == null ? undefined : `${total[color].pct}%`,
                  swatch: <Swatch className={ADHERENCE_BAR[color]} />,
                }))}
              />
            </>
          ) : (
            <p className="m-0 text-[11.5px] leading-snug text-[#7b8799]">
              {adherenceUnavailableHint(adherence, t)}
            </p>
          )}
        </div>

        {/* O plano de contas fica ao lado da aderência, como no Ato 1: o donut e, ao lado
            dele, cada status com letra, rótulo, número e %. */}
        <div className="flex min-w-0 flex-col gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {t("Chart of accounts adherence")}
          </div>

          {coa ? (
            <>
              <div className="flex flex-wrap items-center gap-4">
                <Donut
                  size={144}
                  thickness={12}
                  gap={2}
                  sliceTitles
                  slices={COA_STATUSES.map((status) => ({
                    key: status,
                    label: `${status} · ${statusLabels[status]}`,
                    color: COA_STATUS_COLORS[status],
                    value: coa.byStatus[status],
                  }))}
                  ariaLabel={t("Accounts by adherence status")}
                >
                  <span
                    data-testid="summary-coa-adherence"
                    className="text-[22px] font-semibold leading-none text-[#08254c]"
                  >
                    {coa.adherencePct == null ? NO_VALUE : formatPercent(coa.adherencePct)}
                  </span>
                  <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.08em] text-[#7b8799]">
                    {t("adherence")}
                  </span>
                </Donut>

                <ul className="m-0 flex min-w-[190px] list-none flex-col gap-1.5 p-0">
                  {COA_STATUSES.map((status) => (
                    <li
                      key={status}
                      data-testid={`summary-coa-${status}`}
                      className="flex items-center gap-2 text-[12px]"
                    >
                      <CoaStatusBadge status={status} />
                      <span className="min-w-0 flex-1 truncate text-[#08254c]">
                        {statusLabels[status]}
                      </span>
                      <span className="font-mono tabular-nums text-[#08254c]">
                        {formatCount(coa.byStatus[status])}
                      </span>
                      <span className="w-12 text-right font-mono tabular-nums text-[#7b8799]">
                        {coa.accounts > 0
                          ? formatPercent((coa.byStatus[status] / coa.accounts) * 100)
                          : NO_VALUE}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="m-0 text-[11px] leading-snug text-[#7b8799]">
                {t("{{count}} accounts in the client's chart compared with the Public YCOA", {
                  count: formatCount(coa.accounts),
                })}
              </p>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span
                data-testid="summary-coa-adherence"
                className="text-[22px] font-semibold leading-none text-[#08254c]"
              >
                {NO_VALUE}
              </span>
              <small className="text-[11px] text-[#7b8799]">
                {t("The chart of accounts is not available yet.")}
              </small>
            </div>
          )}
        </div>
      </div>
    </SummaryBlock>
  );
}
